import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Tags, Plus, X, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";

export const KenmerkenPanel = ({ orgId }) => {
  const [kenmerken, setKenmerken] = useState([]);
  const [defs, setDefs] = useState([]);
  const [suggesties, setSuggesties] = useState([]);
  const [open, setOpen] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [edit, setEdit] = useState([]);

  const load = async () => {
    const [o, d, s] = await Promise.all([
      api.get(`/organisaties/${orgId}`),
      api.get("/kenmerk-definities"),
      api.get(`/organisaties/${orgId}/kenmerk-suggesties`),
    ]);
    setKenmerken(o.data.kenmerken || []);
    setDefs(d.data || []);
    setSuggesties(s.data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [orgId]);

  // groepeer per categorie
  const perCat = {};
  for (const k of kenmerken) (perCat[k.categorie] = perCat[k.categorie] || []).push(k);

  const openEdit = () => {
    setEdit(kenmerken.map(k => ({ categorie: k.categorie, waarde: k.waarde })));
    setOpen(true);
  };
  const addRow = () => setEdit(e => [...e, { categorie: defs[0]?.categorie || "", waarde: "" }]);
  const setRow = (i, patch) => setEdit(e => e.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  const delRow = (i) => setEdit(e => e.filter((_, idx) => idx !== i));
  const saveEdit = async () => {
    const items = edit.filter(r => r.categorie && r.waarde);
    const { data } = await api.put(`/organisaties/${orgId}/kenmerken`, { kenmerken: items });
    setKenmerken(data.kenmerken); setOpen(false);
    toast.success("Kenmerken opgeslagen");
  };

  const runAi = async () => {
    setAiBusy(true);
    try {
      const { data } = await api.post(`/organisaties/${orgId}/kenmerken/ai-suggest`);
      setSuggesties(data.suggesties || []);
      toast.success(data.suggesties?.length ? `${data.suggesties.length} suggestie(s)` : "Geen nieuwe suggesties");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI-suggestie mislukt");
    } finally { setAiBusy(false); }
  };
  const approve = async (s) => {
    await api.post(`/kenmerk-suggesties/${s.id}/goedkeuren`);
    setSuggesties(x => x.filter(i => i.id !== s.id));
    await load(); toast.success("Toegevoegd");
  };
  const reject = async (s) => {
    await api.post(`/kenmerk-suggesties/${s.id}/afwijzen`);
    setSuggesties(x => x.filter(i => i.id !== s.id));
  };

  const waardenVoor = (cat) => defs.find(d => d.categorie === cat)?.waarden || [];

  return (
    <div className="space-y-3 pt-2 border-t border-border" data-testid="kenmerken-panel">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          <Tags className="h-3.5 w-3.5" /> Kenmerken
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={runAi} disabled={aiBusy} data-testid="kenmerk-ai-btn">
            <Sparkles className="h-3.5 w-3.5 mr-1" />{aiBusy ? "Bezig…" : "AI"}
          </Button>
          <Button size="sm" variant="ghost" className="h-7 px-2" onClick={openEdit} data-testid="kenmerk-edit-btn">
            <Plus className="h-3.5 w-3.5 mr-1" />Bewerk
          </Button>
        </div>
      </div>

      {kenmerken.length === 0 && <div className="text-sm text-muted-foreground italic">Nog geen kenmerken.</div>}
      {Object.entries(perCat).map(([cat, items]) => (
        <div key={cat} data-testid={`kenmerk-cat-${cat}`}>
          <div className="text-[11px] text-muted-foreground mb-1">{cat}</div>
          <div className="flex flex-wrap gap-1.5">
            {items.map((k, i) => (
              <Badge key={i} variant={k.bron === "ai" ? "default" : "secondary"} className="font-normal">
                {k.waarde}
              </Badge>
            ))}
          </div>
        </div>
      ))}

      {suggesties.length > 0 && (
        <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2" data-testid="kenmerk-suggesties">
          <div className="flex items-center gap-1.5 text-xs font-semibold text-primary">
            <Sparkles className="h-3.5 w-3.5" /> AI-suggesties (ter beoordeling)
          </div>
          {suggesties.map(s => (
            <div key={s.id} className="flex items-start justify-between gap-2 text-sm">
              <div className="flex-1">
                <span className="text-[11px] text-muted-foreground">{s.categorie}: </span>
                <span className="font-medium">{s.waarde}</span>
                {s.reden && <div className="text-xs text-muted-foreground">{s.reden}</div>}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-green-600" onClick={() => approve(s)} data-testid={`kenmerk-approve-${s.id}`}><Check className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-destructive" onClick={() => reject(s)} data-testid={`kenmerk-reject-${s.id}`}><X className="h-4 w-4" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Kenmerken bewerken</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[50vh] overflow-y-auto">
            {edit.map((r, i) => (
              <div key={i} className="flex items-center gap-2">
                <Select value={r.categorie} onValueChange={(v) => setRow(i, { categorie: v, waarde: "" })}>
                  <SelectTrigger className="flex-1" data-testid={`kenmerk-row-cat-${i}`}><SelectValue placeholder="Categorie" /></SelectTrigger>
                  <SelectContent>{defs.map(d => <SelectItem key={d.id} value={d.categorie}>{d.categorie}</SelectItem>)}</SelectContent>
                </Select>
                {waardenVoor(r.categorie).length > 0 ? (
                  <Select value={r.waarde} onValueChange={(v) => setRow(i, { waarde: v })}>
                    <SelectTrigger className="flex-1" data-testid={`kenmerk-row-val-${i}`}><SelectValue placeholder="Waarde" /></SelectTrigger>
                    <SelectContent>{waardenVoor(r.categorie).map(w => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
                  </Select>
                ) : (
                  <Input className="flex-1" value={r.waarde} placeholder="Waarde" onChange={(e) => setRow(i, { waarde: e.target.value })} data-testid={`kenmerk-row-valinput-${i}`} />
                )}
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-destructive" onClick={() => delRow(i)}><X className="h-4 w-4" /></Button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={addRow} data-testid="kenmerk-addrow-btn"><Plus className="h-4 w-4 mr-1" />Kenmerk toevoegen</Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Annuleren</Button>
            <Button onClick={saveEdit} data-testid="kenmerk-save-btn">Opslaan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
